const MAX_AUTO_WIDTH = 480;

export function parseWidth(width) {
  if (typeof width === "number" && Number.isFinite(width)) return width;
  if (typeof width === "string" && /^\d+(\.\d+)?(px)?$/.test(width)) return parseFloat(width);

  return undefined;
}

function estimateWidth(column) {
  return Math.min(Math.max(String(column.caption || "").length * 8 + 56, 90), 240);
}

export function uniqueColumns(columns) {
  const seen = new Map();

  return columns.map(column => {
    const count = seen.get(column.id) || 0;

    seen.set(column.id, count + 1);

    return count ? { ...column, id: `${column.id}__${count}` } : column;
  });
}

export function columnsMap(columns) {
  const map = {};

  columns.forEach(column => {
    if (column.dataField && !map[column.dataField]) map[column.dataField] = column;
    if (column.name && !map[column.name]) map[column.name] = column;
  });

  return map;
}

export function computeLayout({ dataColumns, commandWidth, getState, natural, containerWidth }) {
  const widths = {};
  const autoColumns = [];
  let fixedTotal = commandWidth;
  let autoTotal = 0;

  dataColumns.forEach(column => {
    const state = getState(column);
    const explicit = state.width ?? parseWidth(column.width);
    const minWidth = column.minWidth ?? 0;

    if (explicit) {
      widths[column.id] = Math.max(explicit, minWidth);
      fixedTotal += widths[column.id];
      return;
    }

    const base = Math.max(
      Math.min(natural[column.id] ?? estimateWidth(column), MAX_AUTO_WIDTH),
      minWidth
    );

    widths[column.id] = base;
    autoTotal += base;
    autoColumns.push(column);
  });

  let filler = 0;

  if (containerWidth > 0) {
    const available = containerWidth - fixedTotal;

    if (autoColumns.length && available > 0 && available >= autoTotal) {
      const ratio = available / autoTotal;
      let assigned = 0;

      autoColumns.forEach((column, index) => {
        const width =
          index === autoColumns.length - 1
            ? available - assigned
            : Math.floor(widths[column.id] * ratio);

        widths[column.id] = width;
        assigned += width;
      });
    } else if (autoColumns.length && available > 0) {
      const pending = autoColumns.slice().sort((a, b) => widths[a.id] - widths[b.id]);
      let remaining = available;

      pending.forEach((column, index) => {
        const share = Math.floor(remaining / (pending.length - index));
        const width = Math.max(Math.min(widths[column.id], share), column.minWidth ?? 0);

        widths[column.id] = width;
        remaining -= width;
      });
    } else if (!autoColumns.length && available > 0) filler = available;
  }

  const total = Object.values(widths).reduce((sum, width) => sum + width, commandWidth) + filler;

  return { widths, filler, total };
}

export function buildViewColumns(commandColumns, visibleDataColumns, columnsState, layout) {
  const left = [];
  const middle = [];
  const right = [];

  visibleDataColumns.forEach(column => {
    const columnState = columnsState[column.id];
    const viewColumn = {
      kind: "data",
      key: column.id,
      column,
      width: layout.widths[column.id],
      sortOrder: columnState?.sortOrder,
      sortIndex: columnState?.sortIndex,
    };

    if (columnState?.fixed && columnState.fixedPosition === "right")
      right.push({ ...viewColumn, fixed: "right" });
    else if (columnState?.fixed) left.push({ ...viewColumn, fixed: "left" });
    else middle.push(viewColumn);
  });

  if (left.length) left[left.length - 1].edge = "left";
  if (right.length) right[0].edge = "right";

  const list = [
    ...commandColumns.map(column => ({ ...column, fixed: "left" })),
    ...left,
    ...middle,
  ];

  if (layout.filler > 0) list.push({ kind: "filler", key: "__filler", width: layout.filler });

  list.push(...right);

  let leftOffset = 0;

  list.forEach((viewColumn, index) => {
    viewColumn.index = index;

    if (viewColumn.fixed === "left") {
      viewColumn.offset = leftOffset;
      leftOffset += viewColumn.width || 0;
    }
  });

  let rightOffset = 0;

  for (let index = list.length - 1; index >= 0; index -= 1)
    if (list[index].fixed === "right") {
      list[index].offset = rightOffset;
      rightOffset += list[index].width || 0;
    }

  return list;
}
